/* eslint-disable no-unused-vars */
/* eslint-disable no-undef */
import React, { useEffect, useState } from 'react';
import { API_BASE_URL, AUTH_ENDPOINTS } from '../utils/constants';
import { useNavigate, useLocation } from 'react-router-dom';
import { Loader2, AlertTriangle, X } from 'lucide-react';

const InvalidEmailModal = ({ onClose, onTryDifferent, onEnterManually }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="flex justify-between items-center p-4 border-b">
          <h2 className="text-xl font-semibold">Invalid email</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6">
          <div className="flex flex-col items-center text-center gap-4">
            <div className="bg-yellow-50 p-4 rounded-full">
              <AlertTriangle className="h-8 w-8 text-yellow-500" />
            </div>
            
            <h3 className="text-lg font-semibold">
              This is not a work account
            </h3>

            <p className="text-gray-600">
              Please use an email address whose domain refers to your company
            </p>

            <div className="w-full space-y-2 my-4">
              <div className="flex items-center gap-2 text-red-500">
                <X className="h-4 w-4" />
                <span>@gmail.com</span>
              </div>
              <div className="flex items-center gap-2 text-green-500">
                <span className="font-bold">✓</span>
                <span>@yourcompany.com</span>
              </div>
            </div>

            <div className="flex flex-col w-full gap-4 mt-4">
              <button
                onClick={onTryDifferent}
                className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Try a different account
              </button>
              
              <div className="text-center text-gray-500">or</div>
              
              <button
                onClick={onEnterManually}
                className="w-full border border-gray-300 py-2 px-4 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Enter email manually
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const OAuthReturn = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState('');
  const [showInvalidEmailModal, setShowInvalidEmailModal] = useState(false);

  useEffect(() => {
    const handleOAuthReturn = async () => {
      console.log('OAuth return handler started');
      console.log('Current location:', location);

      try {
        // Get the state parameter from the URL
        const params = new URLSearchParams(location.search);
        const state = params.get('state');
        console.log('State from URL:', state);

        // Check for the special NOT_A_WORK_ACCOUNT_EXEMPTION state
        if (state === 'NOT_A_WORK_ACCOUNT_EXEMPTION') {
          setShowInvalidEmailModal(true);
          return;
        }

        if (!state) {
          throw new Error('No state parameter found');
        }

        // Rest of the normal OAuth flow...
        const storedState = sessionStorage.getItem('googleOAuthState');
        console.log('Stored state:', storedState);

        if (!storedState) {
          throw new Error('No stored state found. Please try logging in again.');
        }

        sessionStorage.removeItem('googleOAuthState');
        const { userId, refreshToken } = decodeStateParameter(state);
        
        localStorage.setItem('userId', userId);
        localStorage.setItem('refreshToken', refreshToken);
        
        navigate('/dashboard', { replace: true });
      } catch (error) {
        console.error('OAuth return handling error:', error);
        setError(error.message || 'Authentication failed. Please try again.');

        setTimeout(() => {
          navigate('/login', { replace: true });
        }, 3000);
      }
    };

    handleOAuthReturn();
  }, [navigate, location]);

  const handleTryDifferent = () => {
    // Redirect to Google login endpoint
    window.location.href = `${API_BASE_URL}/api/auth/user/login/google`;
  };

  const handleEnterManually = () => {
    navigate('/login', { replace: true });  // Navigate to manual email entry
  };

  const handleModalClose = () => {
    navigate('/login', { replace: true });  // Return to login page
  };

  if (showInvalidEmailModal) {
    return (
      <InvalidEmailModal
        onClose={handleModalClose}
        onTryDifferent={handleTryDifferent}
        onEnterManually={handleEnterManually}
      />
    );
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-screen">
      {error ? (
        <div className="text-red-600 text-center p-4">
          <p className="mb-2">{error}</p>
          <p className="text-sm">Redirecting to login page...</p>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          <p>Completing sign in...</p>
        </div>
      )}
    </div>
  );
};

export default OAuthReturn;